/**
 * «Titolo del giorno» — sottomenu di Scopri (SPEC-020 §2).
 *
 * Tre viste: la scheda di oggi, lo storico, le impostazioni.
 *
 * ## La regola che governa questa pagina
 *
 * Il modello **separa** ciò che ha verificato su una fonte da ciò che sta
 * inferendo, e la pagina deve mantenere quella separazione visibile — altrimenti
 * la distinzione esiste solo nel database e l'utente legge tutto come fatto.
 * Da qui: le prove hanno il loro URL cliccabile accanto, le inferenze stanno in
 * un blocco a parte e dichiarato, e la confidenza è sempre accompagnata dalla
 * ragione che il modello ha dato.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, EmptyState, Field, Select, Spinner, Switch } from '@/ui';
import { ASSET_DOMAIN_LABELS, type AssetDomain } from '@/lib/asset-domain';
import { formatDateTime } from '@/lib/format';
import { usePortfolios } from '@/features/portfolios/api';
import {
  useCatalogSearch,
  useDailyPickLatest,
  useDailyPickRun,
  useDailyPickRuns,
  useLinkDailyPickResult,
  useRunDailyPickNow,
  useUpdateDailyPickSettings,
  type CatalogHit,
  type DailyPickResult,
  type DailyPickRun,
  type DailyPickSettings,
} from './api';
import './daily-pick.css';

const STATUS_LABEL: Record<string, string> = {
  queued: 'in coda',
  running: 'in corso',
  completed: 'completata',
  failed: 'fallita',
};

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'positive' | 'negative'> = {
  queued: 'neutral',
  running: 'accent',
  completed: 'positive',
  failed: 'negative',
};

const CONFIDENCE_TONE: Record<string, 'neutral' | 'accent' | 'positive' | 'negative'> = {
  alta: 'positive',
  media: 'accent',
  bassa: 'negative',
};

type Tab = 'oggi' | 'storico' | 'impostazioni';

const DOMAIN_OPTIONS: Array<{ value: AssetDomain; label: string }> = [
  { value: 'stock', label: ASSET_DOMAIN_LABELS.stock },
  { value: 'crypto', label: ASSET_DOMAIN_LABELS.crypto },
];

export function DailyPickPage() {
  const [tab, setTab] = useState<Tab>('oggi');
  // SPEC-026 §6.4: due schede — Borsa e Crypto — ognuna con le proprie
  // impostazioni, la propria scheda del giorno e il proprio storico.
  const [domain, setDomain] = useState<AssetDomain>('stock');
  const latest = useDailyPickLatest(domain);
  const runNow = useRunDailyPickNow(domain);

  if (latest.isLoading) return <Spinner label="Carico il titolo del giorno…" />;
  if (latest.isError) {
    return <Alert variant="error" title="Non riesco a leggere il titolo del giorno" />;
  }

  const settings = latest.data?.settings;
  const run = latest.data?.run ?? null;
  const inCorso = run?.status === 'queued' || run?.status === 'running';

  return (
    <div className="daily-pick">
      <header className="daily-pick__head">
        <div>
          <h1>Titolo del giorno</h1>
          {domain === 'crypto' ? (
            <p className="daily-pick__lead">
              Una ricerca al giorno per la criptovaluta con un <strong>catalizzatore datato</strong>{' '}
              nelle 24 ore successive: un aggiornamento di protocollo, uno sblocco di token, un listing,
              una decisione regolatoria. Solo fra le prime 200 per capitalizzazione. Non è un segnale di acquisto.
            </p>
          ) : (
            <p className="daily-pick__lead">
              Una ricerca al giorno per il titolo con un <strong>catalizzatore datato</strong> sulla
              seduta successiva: una trimestrale con l&apos;orario, un dato macro, una scadenza
              regolatoria. Non è un segnale di acquisto.
            </p>
          )}
          <div className="daily-pick__domain" role="radiogroup" aria-label="Ambito della scheda">
            {DOMAIN_OPTIONS.map((option) => (
              <label key={option.value} className={domain === option.value ? 'active' : ''}>
                <input
                  type="radio"
                  name="daily-pick-domain"
                  value={option.value}
                  checked={domain === option.value}
                  onChange={() => setDomain(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
        <Button
          onClick={() => runNow.mutate()}
          disabled={runNow.isPending || inCorso}
          variant="secondary"
        >
          {inCorso ? 'Ricerca in corso…' : 'Cerca adesso'}
        </Button>
      </header>

      {runNow.isError && (
        <Alert variant="error" title="Non è stato possibile avviare la ricerca">
          {runNow.error instanceof Error ? runNow.error.message : 'Errore sconosciuto'}
        </Alert>
      )}

      {settings && !settings.enabled && tab !== 'impostazioni' && (
        <Alert variant="info" title="La ricerca automatica è spenta">
          Puoi lanciarla a mano con «Cerca adesso». Per farla partire da sola ogni giorno,
          accendila nelle <button className="linklike" onClick={() => setTab('impostazioni')}>impostazioni</button>.
        </Alert>
      )}

      <nav className="daily-pick__tabs" aria-label="Viste">
        {(['oggi', 'storico', 'impostazioni'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className={tab === key ? 'daily-pick__tab daily-pick__tab--on' : 'daily-pick__tab'}
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
          >
            {key === 'oggi' ? 'Oggi' : key === 'storico' ? 'Storico' : 'Impostazioni'}
          </button>
        ))}
      </nav>

      {tab === 'oggi' && <TodayView run={run} settings={settings} />}
      {tab === 'storico' && <HistoryView domain={domain} settings={settings} />}
      {tab === 'impostazioni' && settings && <SettingsView key={domain} domain={domain} settings={settings} />}
    </div>
  );
}

function TodayView({ run, settings }: { run: DailyPickRun | null; settings?: DailyPickSettings }) {
  if (!run) {
    return (
      <EmptyState
        icon="🗓️"
        title="Nessuna ricerca ancora"
        description={
          settings?.enabled
            ? `La prossima parte alle ${settings.runAtLocalTime} (${settings.timezone}).`
            : 'Lancia la prima con «Cerca adesso», oppure accendi la ricerca automatica nelle impostazioni.'
        }
      />
    );
  }

  return <RunDetail run={run} minConfidence={settings?.minConfidence ?? 70} />;
}

/**
 * Un run con la sua scheda, i suoi numeri e i suoi titoli.
 *
 * Estratto da `TodayView` perche' lo **storico mostra la stessa cosa**: due
 * rese separate sarebbero divergute alla prima aggiunta di un campo, e la
 * domanda a cui lo storico deve rispondere — «cosa aveva trovato quel giorno?»
 * — e' identica a quella di oggi.
 */
function RunDetail({ run, minConfidence }: { run: DailyPickRun; minConfidence: number }) {
  return (
    <>
      <Card
        title={`Seduta del ${run.tradingDay}`}
        subtitle={`${run.trigger === 'manual' ? 'Avviata a mano' : 'Pianificata'} · modalità ${run.mode}${run.modelId ? ` · ${run.modelId}` : ''}`}
        action={<Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>{STATUS_LABEL[run.status] ?? run.status}</Badge>}
      >
        {run.status === 'running' || run.status === 'queued' ? (
          <Spinner label="Il modello sta cercando: di solito servono due o tre minuti." />
        ) : null}

        {run.status === 'failed' && (
          <Alert variant="error" title="La ricerca non è riuscita">
            {run.errorMessage ?? 'Nessun dettaglio disponibile.'}
          </Alert>
        )}

        {run.status === 'completed' && run.emptyReason && (
          // Un esito vuoto dice PERCHÉ: senza, si legge come un guasto.
          <Alert variant="info" title="Nessun titolo per questa seduta">
            {run.emptyReason}
          </Alert>
        )}

        {run.stats && <RunStats run={run} />}
      </Card>

      {(run.results ?? []).map((result) => (
        <PickCard key={result.id} result={result} minConfidence={minConfidence} runId={run.id} />
      ))}
    </>
  );
}

function RunStats({ run }: { run: DailyPickRun }) {
  const stats = run.stats ?? {};
  const moves = stats.moves ?? [];
  return (
    <div className="daily-pick__stats">
      <span>{stats.sourcesVisited ?? 0} fonti consultate</span>
      {stats.searches ? <span>{stats.searches} ricerche</span> : null}
      {stats.pagesFetched?.length ? <span>{stats.pagesFetched.length} pagine aperte</span> : null}
      {run.completedAt ? <span>conclusa {formatDateTime(run.completedAt)}</span> : null}
      {moves.length > 0 && (
        <details className="daily-pick__moves">
          {/* Le mosse sono «come ha condotto l'indagine»: nella modalità
              one-shot non esistono, perché le ricerche le fa il provider dentro
              la stessa richiesta e non ce le racconta. */}
          <summary>Come ha cercato ({moves.length} mosse)</summary>
          <ol>
            {moves.map((move, index) => (
              <li key={index}>
                <code>{move.tool}</code>{' '}
                {typeof move.args.query === 'string'
                  ? `«${move.args.query}»`
                  : typeof move.args.url === 'string'
                    ? move.args.url
                    : null}{' '}
                <span className="daily-pick__muted">→ {move.outcome}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

/**
 * Aggancio manuale di una scheda a un titolo a catalogo (F-128).
 *
 * Serve perche' l'aggancio automatico puo' comunque mancare: il catalogo scrive
 * il nome in un modo che nessuna normalizzazione riconcilia, o il modello ha
 * nominato la societa' in un'altra lingua. Senza una via manuale quella scheda
 * resta inservibile — nove ricerche pagate e nessun titolo su cui agire.
 *
 * Ricerca esplicita e non a ogni tasto: si digita e si preme Invio o «Cerca».
 */
function LinkToCatalog({
  resultId,
  link,
}: {
  resultId: string;
  /**
   * La mutation vive nella card e non qui (F-166): appena l'aggancio riesce
   * questo componente sparisce — la riga diventa «a catalogo» — e l'esito
   * della promozione deve sopravvivergli per essere letto.
   */
  link: ReturnType<typeof useLinkDailyPickResult>;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const hits = useCatalogSearch(query);

  if (!open) {
    return (
      <button type="button" className="linklike" onClick={() => setOpen(true)}>
        Aggancia a mano
      </button>
    );
  }

  return (
    <div className="daily-pick__link">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(text);
        }}
      >
        <Field
          label="Cerca a catalogo"
          value={text}
          onChange={(event) => setText(event.target.value)}
          hint="Nome, simbolo o ISIN. Almeno due caratteri."
        />
      </form>
      {hits.isFetching && <Spinner label="Cerco…" />}
      {link.isError && (
        <Alert variant="error" title="Aggancio non riuscito">
          {link.error instanceof Error ? link.error.message : 'Errore imprevisto'}
        </Alert>
      )}
      <p className="daily-pick__muted">
        Scegliendo un titolo l'aggancio è completo: watchlist, wishlist con scadenza a fine seduta e tag «di
        oggi», senza soglia di confidenza — la decisione è tua.
      </p>
      {query.trim().length >= 2 && !hits.isFetching && (hits.data?.items.length ?? 0) === 0 && (
        // ⚠️ Detto esplicitamente: senza, l'assenza di risultati si legge come
        // un guasto della ricerca invece che come «non e' a catalogo».
        <p className="daily-pick__muted">
          Nessun titolo a catalogo per «{query}». Se manca del tutto, va prima aggiunto: qui si
          collega soltanto a ciò che esiste già.
        </p>
      )}
      <ul className="daily-pick__hits">
        {(hits.data?.items ?? []).map((hit: CatalogHit) => (
          <li key={hit.id}>
            <button
              type="button"
              className="linklike"
              disabled={link.isPending}
              onClick={() => link.mutate({ resultId, instrumentId: hit.id })}
            >
              {hit.name}
            </button>{' '}
            <span className="daily-pick__muted">
              {[hit.symbol, hit.isin, hit.currency].filter(Boolean).join(' · ')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PickCard({
  result,
  minConfidence,
  runId,
}: {
  result: DailyPickResult;
  minConfidence: number;
  runId: string;
}) {
  const link = useLinkDailyPickResult(runId);
  const sotto = result.confidenceScore < minConfidence;
  return (
    <Card
      title={`${result.rank}. ${result.name}`}
      subtitle={[result.tickerHint, result.exchangeHint, result.currencyHint].filter(Boolean).join(' · ') || undefined}
      action={
        <Badge tone={CONFIDENCE_TONE[result.confidenceLevel] ?? 'neutral'}>
          confidenza {result.confidenceScore}
        </Badge>
      }
    >
      <p className="daily-pick__catalyst">
        <strong>Catalizzatore:</strong> {result.catalystWhat}
        {result.catalystDate ? ` — ${result.catalystDate}` : null}
        {result.catalystTime ? ` (${result.catalystTime})` : null}
        {result.catalystSourceUrl && (
          <>
            {' '}
            <a href={result.catalystSourceUrl} target="_blank" rel="noreferrer noopener">
              fonte
            </a>
          </>
        )}
      </p>

      {result.description && <p>{result.description}</p>}
      {result.assessment && (
        <p>
          <strong>Valutazione:</strong> {result.assessment}
        </p>
      )}

      {result.whatToWatch.length > 0 && (
        <>
          <h4>Cosa guardare</h4>
          <ul>
            {result.whatToWatch.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </>
      )}

      {result.risks.length > 0 && (
        <>
          <h4>Rischi</h4>
          <ul>
            {result.risks.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </>
      )}

      {result.confidenceRationale && (
        <p className="daily-pick__muted">
          <strong>Perché questa confidenza:</strong> {result.confidenceRationale}
        </p>
      )}

      {result.evidence.length > 0 && (
        <>
          <h4>Prove</h4>
          <ul className="daily-pick__evidence">
            {result.evidence.map((item, index) => (
              <li key={index}>
                {item.claim}{' '}
                <a href={item.sourceUrl} target="_blank" rel="noreferrer noopener">
                  fonte
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="daily-pick__footer">
        {result.instrument ? (
          <>
            <Badge tone="positive">a catalogo</Badge>
            <Link to={`/instruments/${result.instrument.id}`}>{result.instrument.name}</Link>
            {result.instrument.trading212Ticker ? (
              <Badge tone="neutral">{result.instrument.trading212Ticker}</Badge>
            ) : (
              // Senza ticker del broker l'auto-trading non può eseguire: è uno
              // stato normale, e va detto qui invece di scoprirlo al primo ordine.
              <Badge tone="accent">senza ticker broker</Badge>
            )}
          </>
        ) : (
          <>
            <Badge tone="negative">non agganciato a un titolo a catalogo</Badge>
            {/* ⚠️ Il motivo a schermo e non solo nel tooltip: «il catalogo non lo
                conosce» e «un filtro lo ha scartato» chiedono due azioni diverse,
                e un tooltip su un badge non si trova se non lo si cerca. */}
            {result.resolutionNote && (
              <span className="daily-pick__muted">{result.resolutionNote}</span>
            )}
            <LinkToCatalog resultId={result.id} link={link} />
          </>
        )}
        {result.wishlistItemId ? (
          <Badge tone="positive">in wishlist</Badge>
        ) : result.promotionNote && !sotto ? (
          // F-180: il motivo salvato dal backend. Prima qui non c'era niente, e
          // un titolo agganciato ma rimasto fuori sembrava semplicemente ignorato.
          <>
            <Badge tone="negative">non in wishlist</Badge>
            <span className="daily-pick__muted">{result.promotionNote}</span>
          </>
        ) : sotto ? (
          <span className="daily-pick__muted">
            sotto la soglia di {minConfidence}: resta nella scheda, non va in wishlist
          </span>
        ) : null}
      </div>
      {/* F-166: l'esito dell'aggancio a mano, in parole. «Agganciato» da solo
          direbbe meta' della storia: cio' che conta e' se e' fra i candidati di
          oggi, e se no, perche'. */}
      {link.data?.promotion && (
        <Alert
          variant={link.data.promotion.wishlisted ? 'info' : 'warning'}
          title={link.data.promotion.wishlisted ? 'Agganciato e candidato per oggi' : 'Agganciato, ma non in wishlist'}
        >
          {link.data.promotion.wishlisted
            ? 'In watchlist, in wishlist con scadenza a fine seduta e con il tag «di oggi».'
            : link.data.promotion.reason}
        </Alert>
      )}
    </Card>
  );
}

function HistoryView({ domain, settings }: { domain: AssetDomain; settings?: DailyPickSettings }) {
  const runs = useDailyPickRuns(domain);
  // ⚠️ L'elenco NON porta i titoli: `/daily-pick/runs` restituisce solo il
  // conteggio, ed e' giusto — sarebbero decine di schede complete per una
  // vista che serve a scegliere. Il dettaglio si chiede per il run aperto.
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useDailyPickRun(openId ?? undefined);

  if (runs.isLoading) return <Spinner label="Carico lo storico…" />;
  const items = runs.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState icon="📭" title="Nessuna esecuzione" description="Lo storico si riempie da sé." />;
  }
  return (
    <>
      <Card title="Esecuzioni passate" flush>
        <ul className="daily-pick__history">
          {items.map((run) => {
            const open = openId === run.id;
            return (
              <li key={run.id}>
                {/* Tutta la riga apre, non un link in fondo: il bersaglio e'
                    l'oggetto di cui si vogliono le informazioni. */}
                <button
                  type="button"
                  className="daily-pick__history-open"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : run.id)}
                >
                  <span className="daily-pick__history-day">{run.tradingDay}</span>
                  <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>
                    {STATUS_LABEL[run.status] ?? run.status}
                  </Badge>
                  <span className="daily-pick__muted">
                    {run.resultsCount ?? 0} titol{(run.resultsCount ?? 0) === 1 ? 'o' : 'i'}
                    {run.emptyReason ? ` · ${run.emptyReason}` : ''}
                    {run.errorMessage ? ` · ${run.errorMessage}` : ''}
                  </span>
                  <span aria-hidden="true" className="daily-pick__history-caret">
                    {open ? '▾' : '▸'}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </Card>

      {openId && detail.isLoading && <Spinner label="Carico la scheda di quella seduta…" />}
      {openId && detail.isError && (
        <Alert variant="error" title="Scheda non disponibile">
          Non è stato possibile rileggere quella seduta.
        </Alert>
      )}
      {openId && detail.data && (
        <RunDetail run={detail.data} minConfidence={settings?.minConfidence ?? 70} />
      )}
    </>
  );
}

function SettingsView({ domain, settings }: { domain: AssetDomain; settings: DailyPickSettings }) {
  const save = useUpdateDailyPickSettings(domain);
  const portfolios = usePortfolios();
  const [form, setForm] = useState<DailyPickSettings>(settings);

  // Le impostazioni salvate tornano dal server: senza questo, dopo un salvataggio
  // il form mostrerebbe ancora ciò che l'utente aveva scritto, anche se il
  // server ha normalizzato qualcosa.
  useEffect(() => setForm(settings), [settings]);

  const set = <K extends keyof DailyPickSettings>(key: K, value: DailyPickSettings[K]): void =>
    setForm((previous) => ({ ...previous, [key]: value }));

  return (
    <Card
      title="Impostazioni"
      action={
        <Button onClick={() => save.mutate(form)} disabled={save.isPending}>
          {save.isPending ? 'Salvo…' : 'Salva'}
        </Button>
      }
    >
      {save.isError && (
        <Alert variant="error" title="Non ho potuto salvare">
          {save.error instanceof Error ? save.error.message : 'Errore sconosciuto'}
        </Alert>
      )}

      <Switch
        label="Esegui la ricerca automaticamente ogni giorno"
        checked={form.enabled}
        onChange={(value) => set('enabled', value)}
      />
      <Field
        label="Ora di esecuzione"
        type="time"
        value={form.runAtLocalTime}
        onChange={(event) => set('runAtLocalTime', event.target.value)}
        hint="Ora locale del fuso qui sotto. Le 20:00 lasciano tutta la sera per leggere la scheda prima della seduta."
      />
      <Field
        label="Fuso orario"
        value={form.timezone}
        onChange={(event) => set('timezone', event.target.value)}
        hint="Nome IANA, es. Europe/Rome."
      />
      <Select
        label="Modalità"
        value={form.mode}
        onChange={(event) => set('mode', event.target.value as DailyPickSettings['mode'])}
        options={[
          { value: 'agentic', label: 'Agentica — cerca a più riprese e apre le pagine (più lenta, fonti migliori)' },
          { value: 'oneshot', label: 'Rapida — una sola ricerca (meno costosa, vede meno)' },
        ]}
      />
      <Field
        label="Quanti titoli al massimo"
        type="number"
        min={1}
        max={3}
        numeric
        value={String(form.maxPicks)}
        onChange={(event) => set('maxPicks', Number(event.target.value))}
        hint="La domanda al modello resta al singolare: questo è solo un tetto, non un obiettivo da riempire."
      />
      <Field
        label="Confidenza minima per la wishlist"
        type="number"
        min={0}
        max={100}
        numeric
        value={String(form.minConfidence)}
        onChange={(event) => set('minConfidence', Number(event.target.value))}
        hint="Misura quanto è certo il catalizzatore, non quanto salirà il titolo. Sotto questa soglia il titolo resta nella scheda ma non diventa un candidato."
      />

      <hr />

      <Switch
        label="Metti i titoli trovati fra i candidati all'acquisto (wishlist)"
        checked={form.autoWishlistEnabled}
        onChange={(value) => set('autoWishlistEnabled', value)}
      />
      {form.autoWishlistEnabled && (
        <Alert variant="warning" title="Questo interruttore porta al trading">
          I titoli sopra soglia diventano candidati all&apos;acquisto sul portafoglio scelto, con
          scadenza alla mezzanotte che chiude la seduta. Da lì l&apos;auto-trading può valutarli con
          le sue regole.
        </Alert>
      )}
      <Select
        label="Portafoglio di destinazione"
        value={form.portfolioId ?? ''}
        onChange={(event) => set('portfolioId', event.target.value || null)}
        placeholder="Scegli un portafoglio"
        // Solo i portafogli che possono ospitare l'ambito (SPEC-026 §6.1): il
        // server rifiuterebbe comunque, ma offrire un conto titoli per una
        // moneta e' offrire un errore.
        options={(portfolios.data?.items ?? [])
          .filter((portfolio) => portfolio.domains.includes(domain))
          .map((portfolio) => ({ value: portfolio.id, label: portfolio.name }))}
        disabled={!form.autoWishlistEnabled}
      />
      <Field
        label="Esposizione massima (importo)"
        value={form.maxExposureAmount ?? ''}
        onChange={(event) => set('maxExposureAmount', event.target.value || null)}
        numeric
        disabled={!form.autoWishlistEnabled}
        hint="Nella valuta del portafoglio. Vuoto = nessun tetto di importo."
      />
      <Field
        label="Fetta massima del portafoglio (%)"
        value={form.maxPortfolioPercentage ?? ''}
        onChange={(event) => set('maxPortfolioPercentage', event.target.value || null)}
        numeric
        disabled={!form.autoWishlistEnabled}
        hint="Percentuale del valore totale. Vuoto = nessun tetto percentuale."
      />
    </Card>
  );
}
